import type { HotelInfo } from '@/types/hotel';

const CACHE_PREFIX = 'hotel-info:';
const LAST_DOMAIN_KEY = 'hotel-last-domain';
const HOTEL_INFO_TTL = 30 * 60 * 1000; // 30 分钟

interface CachedHotel {
  hotel: HotelInfo;
  expiresAt: number;
}

/**
 * 写入酒店信息到 sessionStorage
 * key 绑定 domain，不同酒店天然隔离
 * 同时记录最后有效域名，供 SSR 失败时兜底查找
 */
export function writeHotelCache(hotel: HotelInfo, domain: string): void {
  if (typeof window === 'undefined' || !domain) return;
  try {
    const entry: CachedHotel = { hotel, expiresAt: Date.now() + HOTEL_INFO_TTL };
    sessionStorage.setItem(`${CACHE_PREFIX}${domain}`, JSON.stringify(entry));
    sessionStorage.setItem(LAST_DOMAIN_KEY, domain);
  } catch {
    // sessionStorage 不可用时静默失败（如隐私模式）
  }
}

/**
 * 读取酒店信息缓存
 * 优先用当前 domain 查找；若未命中且 allowFallback=true，回退到最后有效域名查找
 * （解决测试环境 domain 参数丢失导致 key 不一致的问题）
 *
 * 显式指定 domain（如 ?domain= 切换酒店）时应传 allowFallback=false，
 * 精确命中当前 domain，避免回退到其他酒店的缓存造成串号。
 */
export function readHotelCache(domain: string, allowFallback = true): HotelInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    // 1. 用当前 domain 查找
    if (domain) {
      const hit = readEntry(`${CACHE_PREFIX}${domain}`);
      if (hit) return hit;
    }
    // 2. 回退到最后有效域名查找
    if (allowFallback) {
      const lastDomain = sessionStorage.getItem(LAST_DOMAIN_KEY);
      if (lastDomain && lastDomain !== domain) {
        const hit = readEntry(`${CACHE_PREFIX}${lastDomain}`);
        if (hit) return hit;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * 读取最后有效的查询域名（供 BFF 请求在页面 URL 缺 ?domain= 时回退取用）。
 *
 * 与 readHotelCache 的 allowFallback 回退同源：值为 writeHotelCache 写入的查询 domain
 * （即成功解析过酒店的 ?domain= token / 子域名），保证 round-trip。
 * SSR/非浏览器环境返回 null。
 */
export function getLastDomain(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(LAST_DOMAIN_KEY);
  } catch {
    return null;
  }
}

function readEntry(key: string): HotelInfo | null {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  const entry: CachedHotel = JSON.parse(raw);
  if (entry.expiresAt <= Date.now()) {
    sessionStorage.removeItem(key);
    return null;
  }
  return entry.hotel;
}

/**
 * 清空所有酒店缓存
 */
export function clearHotelCache(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.removeItem(LAST_DOMAIN_KEY);
  } catch {
    // 静默失败
  }
}
